import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useConfirm from '../ui/useConfirm';
import '../AdminUsuarios/AdminUsuarios.css';
import './AdminOperacoes.css';

const API = '';

const FORM_VAZIO = {
  codigo: '',
  descricao: '',
  maquina: '',
  multiplos: 1,
  consome_material: true,
  grupo: '',
};

export default function AdminOperacoes() {
  const { user } = useAuth();
  const [confirmEl, confirm] = useConfirm();
  const [operacoes, setOperacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const [modal, setModal] = useState(null); // null | 'criar' | 'editar'
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const carregarOperacoes = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const res = await fetch(`${API}/api/operacoes`, { headers });
      if (res.status === 403) {
        setErro('Acesso negado. Esta página requer autenticação.');
        return;
      }
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setOperacoes(
        [...data].sort((a, b) =>
          String(a.codigo).localeCompare(String(b.codigo), undefined, { numeric: true })
        )
      );
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregarOperacoes();
  }, [carregarOperacoes]);

  const abrirCriar = () => {
    setForm(FORM_VAZIO);
    setErroForm(null);
    setModal('criar');
  };

  const abrirEditar = (op) => {
    setForm({
      codigo: op.codigo,
      descricao: op.descricao,
      maquina: op.maquina,
      multiplos: op.multiplos,
      consome_material: op.consome_material,
      grupo: op.grupo || '',
    });
    setErroForm(null);
    setModal('editar');
  };

  const fecharModal = () => {
    setModal(null);
    setErroForm(null);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErroForm(null);

    const base = {
      descricao: form.descricao.trim(),
      maquina: form.maquina.trim(),
      multiplos: Number(form.multiplos),
      consome_material: form.consome_material,
      grupo: form.grupo.trim() || null,
    };

    try {
      let res;
      if (modal === 'criar') {
        res = await fetch(`${API}/api/operacoes`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ codigo: form.codigo.trim(), ...base }),
        });
      } else {
        res = await fetch(`${API}/api/operacoes/${encodeURIComponent(form.codigo)}`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(base),
        });
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);

      if (modal === 'criar') {
        setOperacoes((prev) =>
          [...prev, data].sort((a, b) =>
            String(a.codigo).localeCompare(String(b.codigo), undefined, { numeric: true })
          )
        );
      } else {
        setOperacoes((prev) => prev.map((op) => (op.codigo === data.codigo ? data : op)));
      }
      fecharModal();
    } catch (e) {
      setErroForm(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const handleToggleAtivo = async (op) => {
    const acao = op.ativo ? 'desativar' : 'ativar';
    if (!await confirm({
      title: `${acao === 'ativar' ? 'Ativar' : 'Desativar'} operação`,
      message: `Deseja ${acao} "${op.codigo} — ${op.descricao}"?`,
      confirmLabel: acao === 'ativar' ? 'Ativar' : 'Desativar',
      confirmVariant: acao === 'ativar' ? 'primary' : 'danger',
    })) return;
    try {
      const res = await fetch(
        `${API}/api/operacoes/${encodeURIComponent(op.codigo)}/toggle-ativo`,
        { method: 'PUT', headers }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);
      setOperacoes((prev) => prev.map((x) => (x.codigo === data.codigo ? data : x)));
    } catch (e) {
      console.error('toggle-ativo:', e);
      alert(`Erro: ${e.message}`);
    }
  };

  return (
    <>
      {confirmEl}
      <h1 className="cw-page-title">Operações</h1>
      <div className="admin-container">
      <div className="admin-header">
        {user?.is_superuser && (
          <button className="btn-primary" onClick={abrirCriar}>
            + Nova operação
          </button>
        )}
      </div>

      {erro && <div className="admin-erro">{erro}</div>}

      {loading ? (
        <div className="admin-loading">Carregando...</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Máquina</th>
                <th>Múltiplos</th>
                <th>Consome Mat.</th>
                <th>Grupo</th>
                <th>Ativo</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {operacoes.map((op) => (
                <tr key={op.codigo}>
                  <td><span className="cod-mono">{op.codigo}</span></td>
                  <td>{op.descricao}</td>
                  <td>{op.maquina}</td>
                  <td style={{ textAlign: 'center' }}>{op.multiplos}</td>
                  <td>
                    <span className={`badge ${op.consome_material ? 'badge-ativo' : 'badge-inativo'}`}>
                      {op.consome_material ? 'Sim' : 'Não'}
                    </span>
                  </td>
                  <td>{op.grupo || '—'}</td>
                  <td>
                    <span className={`badge ${op.ativo ? 'badge-ativo' : 'badge-inativo'}`}>
                      {op.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="acoes">
                    {user?.is_superuser && (
                      <>
                        <button className="btn-sm btn-outline" onClick={() => abrirEditar(op)}>
                          Editar
                        </button>
                        <button
                          className={`btn-sm ${op.ativo ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => handleToggleAtivo(op)}
                        >
                          {op.ativo ? 'Desativar' : 'Ativar'}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{modal === 'criar' ? 'Nova operação' : `Editar — ${form.codigo}`}</h3>
            <form onSubmit={handleSalvar}>
              <label>
                Código *
                <input
                  type="text"
                  required
                  readOnly={modal === 'editar'}
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  placeholder="ex.: 004.2"
                  style={modal === 'editar' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                />
              </label>
              <label>
                Descrição *
                <input
                  type="text"
                  required
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  placeholder="Descrição da operação"
                />
              </label>
              <label>
                Máquina *
                <input
                  type="text"
                  required
                  value={form.maquina}
                  onChange={(e) => setForm({ ...form, maquina: e.target.value })}
                  placeholder="ex.: 1050"
                />
              </label>
              <label>
                Múltiplos
                <input
                  type="number"
                  min="1"
                  value={form.multiplos}
                  onChange={(e) => setForm({ ...form, multiplos: e.target.value })}
                />
              </label>
              <label>
                Grupo
                <input
                  type="text"
                  value={form.grupo}
                  onChange={(e) => setForm({ ...form, grupo: e.target.value })}
                  placeholder="opcional"
                />
              </label>
              <label className="label-checkbox">
                <input
                  type="checkbox"
                  checked={form.consome_material}
                  onChange={(e) => setForm({ ...form, consome_material: e.target.checked })}
                />
                Consome material
              </label>

              {erroForm && <div className="admin-erro">{erroForm}</div>}

              <div className="modal-actions">
                <button type="button" className="btn-outline" onClick={fecharModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={salvando}>
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </>
  );
}
